import { OutlineFactory } from 'web/engine/outline-factory'
import { strictParseTimestamp, timestampToSeconds } from 'web/utils/time-utils'
import { lastItem } from 'web/utils/utils'
import { FIGHT_CATEGORY_ID } from './general/id'
import { Fight, FightEvent, FightEventFlag, FightSegment } from './models'

type ParsedFightParts = {
    fight: Fight
    segments: FightSegment[]
    events: FightEvent[]
}

/**
 * Parses a string outline into a full fight. See the docs for format details.
 */
export function parseFightOutline(params: {
    fightOutlineString: string
    fightName?: string
}): ParsedFightParts {

    const { fightOutlineString, fightName } = params

    const inputLines = sanatizeInput(fightOutlineString)

    if (inputLines.length <= 0) {
        throw new FightParserError({
            code: FightParserError.ErrorCode.EMPTY,
            trace: ''
        })
    }

    // This does the bulk of the job, if we get through here, the input is fine
    // and we can get started building the fight.
    const parsedLines = parseLines(inputLines)

    const fightAssembler = FightAssembler.createNewFight({
        fightName: fightName ?? `Imported fight ${Date.now()}`,
        firstSegmentName: (parsedLines[0].type === "segment")
            ? parsedLines[0].name
            : undefined
    })

    for (let index = 0; index < parsedLines.length; index++) {
        const thisLine = parsedLines[index]

        // If we have an initial segment, it's already processed
        if (index <= 0 && thisLine.type === "segment") {
            continue
        }

        if (thisLine.type === "event") {
            fightAssembler.pushEvent({
                name: thisLine.name,
                fightOffset: thisLine.seconds,
                flags: thisLine.flags ?? []
            })

            continue
        }

        if (thisLine.type === "segment") {
            fightAssembler.pushSegment({
                name: thisLine.name,
                fightOffset: thisLine.seconds,
                condition: thisLine.condition
            })

            continue
        }

        throw Error(`Unhandled line type: ${thisLine.type}`)
    }

    return fightAssembler.finish()
}

function sanatizeInput(fightString: string): string[] {
    return fightString
        // Replace line-breaks with special char
        .replace(/(\r\n|\n|\r)/gm, "¤")
        // Split along special char
        .split("¤")
        // Remove empty strings
        .filter(line => line.trim() !== "")
        // Remove whitespace around each line
        .map(line => line.trim())
}

/**
 * Parses the raw lines, asserting their formatting is valid, and that they're
 * in chronological order. This function contains all of the validation, once
 * you pass this call, the fight _should_ be correctly entered.
 *
 * Throws descriptive errors when parsing fails, otherwise returns array with
 * parsed parts, in the order they were parsed.
 */
function parseLines(fightLines: string[]) {
    let lastTimestamp = 0
    let lineIdentified = false

    /*
        -- DEVELOPER NOTICE --

        This entire thing should've been done in reverse. We sometimes need the
        timestamp from an event when creating a segment. Right now I just
        iterate twice, but you could skip all that if you parsed in backwards.
        In that case the events would always be parsed and ready for the
        segments to use.
    */

    const parsedLines: {
        type: "event" | "segment"
        name: string
        seconds: number
        condition?: string
        flags?: FightEventFlag[]
    }[] = []

    for (let index = 0; index < fightLines.length; index++) {
        const thisLine = fightLines[index]
        const nextLine = fightLines[index + 1]

        if (isEvent(thisLine)) {
            lineIdentified = true

            const parts = parseEvent(thisLine)

            if (parts == null) {
                throw new FightParserError({
                    code: FightParserError.ErrorCode.MALFORMED_LINE,
                    trace: createErrorTrace(index, fightLines)
                })
            }

            if (parts.seconds < lastTimestamp) {
                throw new FightParserError({
                    code: FightParserError.ErrorCode.NOT_CHRONOLOGICAL,
                    trace: createErrorTrace(index, fightLines)
                })
            }

            lastTimestamp = parts.seconds

            parsedLines.push({
                type: "event",
                ...parts
            })
        }

        if (isSegment(thisLine)) {
            lineIdentified = true

            if (nextLine == null || isSegment(nextLine)) {
                throw new FightParserError({
                    code: FightParserError.ErrorCode.EMPTY_SEGMENT,
                    trace: createErrorTrace(index, fightLines)
                })
            }

            const parts = parseSegment(thisLine)

            if (parts == null) {
                throw new FightParserError({
                    code: FightParserError.ErrorCode.MALFORMED_LINE,
                    trace: createErrorTrace(index, fightLines)
                })
            }

            // We don't require a timestamp on segments
            if (parts.seconds != null) {

                // The first segment cannot have a timestamp
                if (index <= 0) {
                    throw new FightParserError({
                        code: FightParserError.ErrorCode.MALFORMED_SEGMENT_ILLEGAL_TIMESTAMP,
                        trace: createErrorTrace(index, fightLines)
                    })
                }

                if (parts.seconds < lastTimestamp) {
                    throw new FightParserError({
                        code: FightParserError.ErrorCode.NOT_CHRONOLOGICAL,
                        trace: createErrorTrace(index, fightLines)
                    })
                }

                lastTimestamp = parts.seconds
            }

            parsedLines.push({
                type: "segment",
                name: parts.name,
                // We need something unique here to check for later when moving
                // the event timestamp
                seconds: parts.seconds ?? -1,
                condition: parts.condition
            })
        }

        // Reset this check for the next iteration
        if (lineIdentified) {
            lineIdentified = false
        } else {
            throw new FightParserError({
                code: FightParserError.ErrorCode.MALFORMED_LINE,
                trace: createErrorTrace(index, fightLines)
            })
        }

    }

    parsedLines.forEach((line, index) => {
        // This segment is missing its timestamp, so we'll grab one from its
        // first event.
        if (line.type === "segment" && line.seconds === -1) {
            line.seconds = parsedLines[index + 1].seconds
        }
    })

    return parsedLines
}

/**
 * Creates a pretty trace, showing where an error occurred, what's before it and
 * what's after it.
 *
 * Returns a string containing the error trace.
 */
export function createErrorTrace(errorAtIndex: number, fightLines: string[]) {

    if (errorAtIndex >= fightLines.length) {
        throw Error(`Error index is out of bounds, ` +
            `error index: ${errorAtIndex}, line count: ${fightLines.length}`)
    }

    // Define the span from which to save lines
    const head = errorAtIndex - 2
    const tail = errorAtIndex + 2

    const errorLines: string[] = []

    for (let index = 0; index < fightLines.length; index++) {
        const line = fightLines[index]

        if (head <= index && tail >= index) {

            if (errorAtIndex === index) {
                errorLines.push(`> ${line}`)
            } else {
                errorLines.push(`  ${line}`)
            }

        }

    }

    return errorLines.join("\n")
}

function isSegment(value: string) {
    return value.startsWith("#")
}

function isEvent(value: string) {
    return parseEvent(value) != null
}

/**
 * Tries to parse the given value as an event. Returns null if parsing fails.
 */
export function parseEvent(value: string) {
    // This is a segment
    if (value.charAt(0) === "#") {
        return null
    }

    // hh:mm:ss.SSS | hh:mm:ss | mm:ss | mmss
    // Also allows for an optional leading ! or -
    const matchObj = value.match(/([!-])?(\d{2}:\d{2}:\d{2}.\d{3}|\d{2}:\d{2}:\d{2}|\d{2}:\d{2}|\d{4})(.*)/)

    if (matchObj == null) {
        return null
    }

    const flagPart = matchObj[1]
    const timePart = matchObj[2]
    const namePart = matchObj[3]

    const emphasizeFlag = flagPart === '!'
    const muteFlag = flagPart === '-'

    const event: {
        seconds: number
        name: string
        flags: FightEventFlag[]
    } = {
        seconds: timestampToSeconds(strictParseTimestamp(timePart)),
        name: namePart.trim(),
        flags: []
    }

    if (emphasizeFlag) {
        event.flags.push(FightEventFlag.Emphasize)
    }
    if (muteFlag) {
        event.flags.push(FightEventFlag.Muted)
    }

    return event
}

/**
 * Tries to parse the given value as a segment. Returns null if parsing fails.
 */
export function parseSegment(value: string) {
    // Not a valid segment
    if (value.charAt(0) !== "#") {
        return null
    }

    // hh:mm:ss.SSS | hh:mm:ss | mm:ss | mmss
    const matchObj = value.substr(1).match(/(\d{2}:\d{2}:\d{2}.\d{3}|\d{2}:\d{2}:\d{2}|\d{2}:\d{2}|\d{4})(.*)/)

    // Didn't find any time/name combo, probably just a name
    if (matchObj == null) {
        const maybeOnlyName = value.substr(1)

        if (maybeOnlyName.trim() !== "") {
            return {
                name: maybeOnlyName.trim(),
                seconds: null
            }
        }

        // We're dead if we find neither time nor name
        return null
    }

    const timePart = matchObj[1]
    const conditionPart = matchObj[2].match(/\w{3}:\d+:\d+$/)
    const namePart = matchObj[2].replace(/\w{3}:\d+:\d+$/, "")

    if (namePart.trim() === "") {
        return null
    }

    return {
        name: namePart.trim(),
        seconds: timestampToSeconds(strictParseTimestamp(timePart)),
        condition: (conditionPart && conditionPart[0]) ?? undefined
    }
}

export class FightParserError extends Error {

    get name() { return this.constructor.name }

    public code: Readonly<string>
    public trace: Readonly<string>

    constructor({ code, trace }: { code: string; trace: string }) {
        super(`Failed during fight parsing with code: ${code}`)

        this.code = code
        this.trace = trace
    }

    public static ErrorCode = {
        /**
         * Input was empty
         */
        EMPTY: "EMPTY",
        /**
         * Lines were not in chronological order
         */
        NOT_CHRONOLOGICAL: "NOT_CHRONOLOGICAL",
        /**
         * Found a segment without any events tied to it
         */
        EMPTY_SEGMENT: "EMPTY_SEGMENT",
        /**
         * Failed to make sense of the line
         */
        MALFORMED_LINE: "MALFORMED_LINE",
        /**
         * First segment cannot have a timestamp
         */
        MALFORMED_SEGMENT_ILLEGAL_TIMESTAMP: "MALFORMED_SEGMENT_ILLEGAL_TIMESTAMP"
    }

}

export class FightAssembler {

    private fight: Fight
    private segments: FightSegment[]
    private events: FightEvent[]

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Private Constructor, use static builder method
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    private constructor(parts: {
        fight: Fight
        segments: FightSegment[]
        events: FightEvent[]
    }) {
        this.fight = parts.fight
        this.segments = parts.segments
        this.events = parts.events
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Static Public API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    /**
     * Creates a new fight and returns an engine instance for it.
     */
    public static createNewFight(opts: {
        fightName: string
        /**
         * Optional name for the first segment
         */
        firstSegmentName?: string
    }) {

        const { fightName, firstSegmentName } = opts

        const fight = OutlineFactory.createFight({
            fightName,
            fightCategoryId: FIGHT_CATEGORY_ID.CUSTOM.Uncategorized
        })

        // The first segment is required and owned by the system
        const firstSegment = OutlineFactory.createSegment({
            fightId: fight.id,
            name: firstSegmentName ?? "Segment 1",
            offset: 0,
            userCreated: false
        })

        // First event is required and owned by the system
        const firstEvent = OutlineFactory.createEvent({
            fightSegmentId: firstSegment.id,
            name: "Pull",
            fightOffset: 0,
            userCreated: false,
            flags: []
        })

        return new FightAssembler(JSON.parse(JSON.stringify({
            fight,
            segments: [firstSegment],
            events: [firstEvent]
        })))
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Public API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    public finish(): {
        fight: Fight
        segments: FightSegment[]
        events: FightEvent[]
    } {
        return {
            fight: this.fight,
            segments: this.segments,
            events: this.events
        }
    }

    public pushSegment(params: {
        name: string
        fightOffset: number
        condition?: string
    }) {
        const segment = OutlineFactory.createSegment({
            fightId: this.fight.id,
            name: params.name,
            offset: params.fightOffset,
            userCreated: true,
            condition: params.condition
        })

        lastItem(this.segments).childId = segment.id
        this.segments.push(segment)

        return this
    }

    /**
     * Adds a new event to the fight, will live under the latest segment.
     */
    public pushEvent(params: {
        name: string
        fightOffset: number
        flags: FightEventFlag[]
    }) {
        const segment = lastItem(this.segments)

        const event = OutlineFactory.createEvent({
            fightSegmentId: segment.id,
            name: params.name,
            fightOffset: params.fightOffset,
            userCreated: true,
            flags: params.flags
        })

        this.events.push(event)

        return this
    }

}
